import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";import { api } from "@/lib/api";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentSuccessPage() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const provider = params.get("provider") || (sessionId?.startsWith("cs_") ? "handypay" : "stripe");
    const navigate = useNavigate();
    const [state, setState] = useState({ status: "checking", payment_status: null, attempts: 0, receipt: null });
    const pollRef = useRef(null);

    useEffect(() => {
        if (!sessionId) {
            setState({ status: "error", payment_status: null });
            return;
        }
        let attempts = 0;
        const statusEndpoint = provider === "handypay"
            ? `/payments/handypay/status/${sessionId}`
            : `/payments/status/${sessionId}`;
        const poll = async () => {
            attempts += 1;
            try {
                const { data } = await api.get(statusEndpoint);
                setState({ status: data.status, payment_status: data.payment_status, attempts, receipt: data.receipt || null });
                if (data.payment_status === "paid") {
                    return;
                }
                if (data.status === "expired" || attempts >= 8) {
                    setState({ status: "expired", payment_status: data.payment_status, attempts, receipt: data.receipt || null });
                    return;
                }
                pollRef.current = setTimeout(poll, 2000);
            } catch {
                if (attempts >= 8) {
                    setState({ status: "error", payment_status: null, attempts, receipt: null });
                    return;
                }
                pollRef.current = setTimeout(poll, 2000);
            }
        };
        poll();
        return () => clearTimeout(pollRef.current);
    }, [sessionId, provider]);

    const isPaid = state.payment_status === "paid";
    const isProcessing = state.status === "checking" || (!isPaid && state.status !== "expired" && state.status !== "error");

    return (
        <div className="min-h-screen bg-background" data-testid="payment-success-page">
            <Header />
            <section className="max-w-3xl mx-auto px-6 lg:px-10 py-32 text-center">
                {isProcessing && (
                    <>
                        <Loader2 size={56} strokeWidth={1} className="gold-text mx-auto animate-spin mb-8" />
                        <div className="overline mb-4">Confirming Payment</div>
                        <h1 className="font-serif text-5xl tracking-tighter">One moment...</h1>
                        <p className="mt-6 text-muted-foreground">We&apos;re confirming your transaction securely.</p>
                    </>
                )}
                {isPaid && (
                    <>
                        <CheckCircle2 size={64} strokeWidth={1} className="gold-text mx-auto mb-8" data-testid="success-icon" />
                        <div className="overline mb-4">Confirmed</div>
                        <h1 className="font-serif text-5xl sm:text-6xl tracking-tighter">
                            Payment <span className="italic gold-grad">received.</span>
                        </h1>
                        <p className="mt-8 text-muted-foreground max-w-md mx-auto leading-relaxed">
                            Thank you. Your booking is confirmed and a SMART professional will be on the way.
                        </p>
                        <VIPOnboardingRedirect isPaid={isPaid} receipt={state.receipt} />

                        {state.receipt && (
                            <div
                                className="mt-10 mx-auto max-w-md text-left border border-white/10 bg-card/50 rounded-sm p-6"
                                data-testid="payment-receipt-block"
                            >
                                <div className="overline mb-4">Receipt</div>
                                {state.receipt.service_name && (
                                    <div className="pb-4 mb-4 border-b border-white/5">
                                        <div className="text-xs text-muted-foreground uppercase tracking-[0.2em]">Service</div>
                                        <div className="font-serif text-lg mt-1" data-testid="receipt-service-name">{state.receipt.service_name}</div>
                                    </div>
                                )}
                                <dl className="space-y-2 text-sm">
                                    {state.receipt.royal_protection_fee > 0 && (
                                        <div className="flex justify-between">
                                            <dt className="text-muted-foreground">Royal Protection &amp; Insurance</dt>
                                            <dd>${state.receipt.royal_protection_fee.toLocaleString()}</dd>
                                        </div>
                                    )}
                                    <div className="flex justify-between" data-testid="receipt-processing-fee">
                                        <dt className="text-muted-foreground">Processing Fee</dt>
                                        <dd>
                                            {state.receipt.processing_fee > 0 ? (
                                                `$${state.receipt.processing_fee.toLocaleString()}`
                                            ) : (
                                                <span className="gold-text text-xs uppercase tracking-[0.2em]">Waived · VIP</span>
                                            )}
                                        </dd>
                                    </div>
                                    {state.receipt.coin_discount_jmd > 0 && (
                                        <div className="flex justify-between text-amber-300">
                                            <dt>SMART Coin discount</dt>
                                            <dd>− ${state.receipt.coin_discount_jmd.toLocaleString()}</dd>
                                        </div>
                                    )}
                                    <div className="flex justify-between pt-3 mt-3 border-t border-white/10">
                                        <dt className="font-serif text-base">Total charged</dt>
                                        <dd className="font-serif text-base gold-text" data-testid="receipt-total">
                                            ${state.receipt.total_amount.toLocaleString()} {state.receipt.currency}
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        )}
                        <div className="mt-6 text-[10px] tracking-[0.25em] uppercase text-muted-foreground/70" data-testid="payment-success-llc">
                            Receipt issued by SMART DEMANDS 24/7 LIMITED
                        </div>
                        <div className="mt-10 flex flex-wrap gap-3 justify-center">
                            <Button
                                onClick={() => navigate("/my-bookings")}
                                className="bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs h-12 px-7"
                                data-testid="view-bookings-btn"
                            >
                                View Bookings
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => navigate("/services")}
                                className="rounded-sm uppercase tracking-[0.2em] text-xs h-12 px-7 border-white/10 bg-transparent"
                                data-testid="back-to-services-btn"
                            >
                                Browse More
                            </Button>
                        </div>
                    </>
                )}
                {(state.status === "expired" || state.status === "error") && !isPaid && (
                    <>
                        <XCircle size={56} strokeWidth={1} className="text-red-400 mx-auto mb-8" />
                        <h1 className="font-serif text-5xl tracking-tighter">Payment incomplete.</h1>
                        <p className="mt-6 text-muted-foreground">The payment session expired or didn't go through.</p>
                        <Button
                            onClick={() => navigate("/services")}
                            className="mt-8 bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs h-12 px-7"
                        >
                            Try Again
                        </Button>
                    </>
                )}
            </section>
        </div>
    );
}

/**
 * When a VIP membership purchase settles (receipt lacks a service_name because
 * package_id transactions don't attach one), auto-nudge the buyer into the
 * schedule wizard so they lock in their auto-booking preferences right away.
 */
function VIPOnboardingRedirect({ isPaid, receipt }) {
    const navigate = useNavigate();
    useEffect(() => {
        if (!isPaid) return;
        // Package purchases return a receipt WITHOUT service_name — that's our
        // signal this was a VIP tier upgrade, not a booking payment.
        const isVipPurchase = receipt && !receipt.service_name;
        if (!isVipPurchase) return;
        const t = setTimeout(() => navigate("/vip/onboarding"), 2500);
        return () => clearTimeout(t);
    }, [isPaid, receipt, navigate]);
    return null;
}
